/**
 * As ENTREGAS do painel da conversa registradas AO VIVO, conforme cada
 * `tool-output-available` de cartão de dados chega (§5.1, kill-switch
 * `PAINEL_PROGRESSO_CONTEXTO_ATIVO`; o `messages.vue` só monta isto ligado).
 *
 * A bolha continua sem desenhar cartão durante o turno ("A ordem do turno é
 * UMA", §10): aqui só se REGISTRA no índice (`registrarArtefatoDaConversa`).
 *
 * Por que um componente sem render, e não um mixin no `messages.vue`: o
 * registro reusa o `renderizaPartesDoTurno` inteiro (mesmo `resolverParte`,
 * `montarCartao`, veredito de schema em `validacoes`) — a mesma regra da bolha
 * persistida, sem duplicar. Como mixin, os ~30 métodos dele entrariam num
 * componente de 1000+ linhas. E fica no `messages.vue`, e não na bolha em
 * streaming, porque ele vive o turno inteiro e vê a virada `chatLoading`
 * true → false, que é quando se decide o que fica.
 *
 * Identidade: a bolha persistida resolve a MESMA parte viva (`parteDaChamada`
 * prefere `toolPartPorId`), então `identidade` = `toolCallId` nos dois lados e
 * o registro do `end` só completa `mensagemId`/`criadoEm` no lugar.
 *
 * Fim do turno: fica o que foi para `tool_calls[]` de alguma mensagem; o resto
 * (cancelado, erro, descartado) sai.
 */
import { renderizaPartesDoTurno } from '@modules/aawz-intelligence-v2/ui-registry/partes-do-turno/renderiza-partes'
import {
  CHAVES_DE_GATE_LEGADO,
  ESTADO_RESOLVIDO,
  chaveDaParte,
} from '@modules/aawz-intelligence-v2/ui-registry/partes-do-turno/partes'
import { artefatosDaMensagem } from '@modules/aawz-intelligence-v2/utils/artefatos-da-conversa'

function idsEmToolCalls(mensagens) {
  const ids = {}
  ;(mensagens || []).forEach(function (mensagem) {
    ;((mensagem && mensagem.tool_calls) || []).forEach(function (chamada) {
      if (chamada && chamada.id) ids[chamada.id] = true
    })
  })
  return ids
}

export default {
  name: 'MessagesEntregasAoVivo',
  mixins: [renderizaPartesDoTurno],
  props: {
    storeModule: { type: String, required: true },
  },
  computed: {
    getters() {
      return (this.$store && this.$store.getters) || {}
    },
    turnoEmVoo() {
      return Boolean(this.getters[this.storeModule + '/chatLoading'])
    },
    // Contrato do mixin: as partes resolvidas do turno, com o mesmo corte de
    // gate legado de `chat-message.vue::partesDeTools`.
    partesParaRenderizar() {
      if (!this.turnoEmVoo) return []
      if (!this.getters[this.storeModule + '/indiceDeArtefatosAtivo']) return []
      const partes = this.getters[this.storeModule + '/toolPartsOrdenadas'] || []
      return partes.filter(function (parte) {
        if (!parte || parte.state !== ESTADO_RESOLVIDO) return false
        const chave = chaveDaParte(parte)
        return Boolean(chave) && !CHAVES_DE_GATE_LEGADO.includes(chave)
      })
    },
    // Só depois do veredito do schema (o `montarCartao` já monta a prévia com
    // veredito `null`; a entrega não pode aparecer e sumir).
    entregasAoVivo() {
      const aprovadas = this.partesResolvidas.filter(this.schemaAprovado)
      return artefatosDaMensagem({ partes: aprovadas, mensagem: null })
    },
  },
  watch: {
    entregasAoVivo(atuais, anteriores) {
      if (!this.turnoEmVoo) {
        this.removerForaDaMensagem(anteriores)
        return
      }
      this.removerSumidas(atuais, anteriores)
      this.registrarNovas(atuais)
    },
  },
  methods: {
    schemaAprovado(cartao) {
      const parte = this.partePorId(cartao.toolCallId)
      if (!parte) return false
      const resolucao = this.resolver(parte)
      if (!resolucao.entrada) return false
      return this.vereditoDoSchema(resolucao.entrada, parte, resolucao.chave) === true
    },
    // Só registra o que ainda não está no índice: numa continuação de tool de
    // front a entrega já veio com `mensagemId`, e regravar zeraria a posição.
    registrarNovas(entregas) {
      const jaNoIndice = {}
      ;(this.getters[this.storeModule + '/artefatosDaConversa'] || []).forEach((artefato) => {
        jaNoIndice[artefato.identidade] = true
      })
      entregas.forEach((entrega) => {
        if (jaNoIndice[entrega.identidade]) return
        this.$store.dispatch(this.storeModule + '/registrarArtefatoDaConversa', entrega)
      })
    },
    removerSumidas(atuais, anteriores) {
      const presentes = {}
      atuais.forEach(function (entrega) {
        presentes[entrega.identidade] = true
      })
      this.remover((anteriores || []).filter(function (entrega) {
        return !presentes[entrega.identidade]
      }))
    },
    removerForaDaMensagem(anteriores) {
      const naMensagem = idsEmToolCalls(this.getters[this.storeModule + '/messages'])
      this.remover((anteriores || []).filter(function (entrega) {
        return !naMensagem[entrega.identidade]
      }))
    },
    remover(entregas) {
      entregas.forEach((entrega) => {
        this.$store.dispatch(this.storeModule + '/removerArtefatoDaConversa', entrega.identidade)
      })
    },
  },
  render() {
    return null
  },
}
